<template>
  <div class="training-story-view">
    <div class="calendar-header">
      <div v-for="(day, index) in calendar" :key="index" class="day-wrapper" @click="selectDay(day, index)">
        <div class="day-name">{{ day.short }}</div>
        <div class="day-button" :class="{ 'day-button--active': activeIndex === index, 'day-button--today': day.today }">
          <v-icon v-if="hasActivity(day)" class="activity-icon">mdi-trophy</v-icon>
          <span v-else>{{ day.date }}</span>
        </div>
      </div>
    </div>

    <div class="progress-container">
      <div class="progress-bar" :style="{ width: activeCount + '%' }"></div>
    </div>

    <v-dialog v-model="datePickerDialog" max-width="500px">
      <v-card>
        <v-date-picker
          full-width
          v-model="activeDate"
          :events="events"
          @change="onDateSelected"
          color="primary"
          locale="CH-DE"
          :allowed-dates="(date) => date <= new Date().toISOString().substr(0, 10)"></v-date-picker>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="datePickerDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <transition name="slide-up">
      <div v-if="selectedDayData" class="story-details-container" :style="{ backgroundColor: storyBackgroundColor }">
        <v-btn @click="closeDetails" absolute icon top right class="close-button">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <h2 class="text-center mb-4" style="text-transform: uppercase; font-weight: 1000; font-style: italic">Dein Training</h2>

        <div v-if="isLoadingDayDetails" class="loading-overlay">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </div>

        <div v-else class="story-content">
          <div class="d-flex justify-center align-center story-title-container">
            <h2 class="story-title">{{ $moment(selectedDayData.date).format("dd, DD. MMMM") }}</h2>
          </div>

          <div class="text-center mb-6">
            <v-btn @click="openDatePicker" text color="primary">
              <v-icon left>mdi-calendar-edit</v-icon>
              Datum wechseln
            </v-btn>
          </div>

          <div v-if="userService.hasRight('gf_cp_instructor')" class="instructor-actions">
            <v-alert text type="info" color="primary" border="left">Hier geht's zu deiner Group Fitness Stundenabrechnung<br /> </v-alert>
            <v-btn class="mt-2" color="primary" @click="showGFCalendar">
              <span v-if="!showGFHours"><v-icon left>mdi-invoice-clock-outline</v-icon>zur Abrechnung</span>
              <span v-else>Zurück</span>
            </v-btn>
          </div>

          <div v-if="showGFHours" class="gf-hours-view">
            <h3 class="gf-title">Stundenabrechnung {{ $moment(selectedDayData.date).format("MMMM") }}</h3>
            <div v-if="fetchingGFHours" class="text-center">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <v-alert v-else-if="!GFHourInfos || !GFHourInfos.list.length" type="info" text> Für diesen Monat wurden keine Stunden gefunden. </v-alert>
            <div v-else>
              <div class="gf-summary">Gesamtdauer: {{ GFHourInfos.totalDuration }} Minuten ({{ GFHourInfos.totalDurationInHours }} Stunden)</div>
              <v-list color="transparent">
                <v-list-item v-for="(course, idx) in GFHourInfos.list" :key="idx">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">{{ course.courseName }}</v-list-item-title>
                    <v-list-item-subtitle>{{ course.courseDate }} - Dauer: {{ course.duration }} Minuten</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>

          <div v-else>
            <div v-if="selectedDayData.checkin" class="activity-card checkin-card">
              <v-icon class="checkin-icon">mdi-map-marker-check</v-icon>
              <div class="card-content">
                <h3>Check-in im Studio</h3>
                <p class="time-range">
                  {{ $moment(selectedDayData.checkin).format("HH:mm") }} Uhr
                  <span v-if="selectedDayData.checkout"> - {{ $moment(selectedDayData.checkout).format("HH:mm") }} Uhr </span>
                </p>
                <p class="duration">{{ trainingDuration }}</p>
              </div>
            </div>

            <div v-for="(activity, i) in combinedActivities" :key="i" class="activity-card">
              <v-tooltip v-if="activity.type === 'Group Fitness' && groupFitnessExecutions[activity.id]" left color="rgba(0,0,0,0.8)">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" class="rating-info-icon">mdi-information</v-icon>
                </template>
                <div class="d-flex flex-column text-center">
                  <span>{{ groupFitnessExecutions[activity.id].count }}x besucht</span>
                  <span>{{ groupFitnessExecutions[activity.id].duration }} Min. im Kurs verbracht</span>
                  <span class="pt-1" v-if="getStarCountForCourse(activity.id) > 0">Ø Bewertung</span>
                  <v-rating
                    v-if="getStarCountForCourse(activity.id) > 0"
                    :value="getStarCountForCourse(activity.id)"
                    color="yellow darken-3"
                    background-color="grey darken-1"
                    empty-icon="$ratingFull"
                    half-increments
                    dense
                    readonly></v-rating>
                </div>
              </v-tooltip>

              <v-img :src="activity.image" class="card-background-img"></v-img>
              <div class="card-overlay-gradient"></div>
              <div class="card-content">
                <span class="activity-type">{{ activity.type }}</span>
                <h3>{{ activity.title }}</h3>

                <div class="stats-grid">
                  <div v-if="activity.duration" class="stat-item">
                    <lottie-player autoplay loop mode="normal" src="/img/lottiefiles/hourGlass.json"></lottie-player>
                    <span>{{ activity.duration }}</span>
                  </div>
                  <div v-if="activity.caloriesBurned" class="stat-item">
                    <lottie-player autoplay loop mode="normal" src="/img/lottiefiles/callorieBurned.json"></lottie-player>
                    <span>{{ activity.caloriesBurned }} kcal</span>
                  </div>
                  <div v-if="activity.liftedWeight" class="stat-item">
                    <lottie-player autoplay loop mode="normal" src="/img/lottiefiles/dumbell.json"></lottie-player>
                    <span><number :from="0" :to="activity.liftedWeight"></number> kg</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!hasAnythingToDo(selectedDayData)" class="no-activity-card">
              <v-icon class="no-activity-icon">mdi-bed</v-icon>
              <h3>Rest Day!</h3>
              <p>An diesem Tag hast du pausiert. Erholung ist genauso wichtig wie Training!</p>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from "../../api"
import userService from "../../services/userService"
import { mapActions } from "vuex"

export default {
  name: "TrainingStoryView",
  data() {
    return {
      userService,
      isLoadingDayDetails: false, // Ersetzt 'loading' für mehr Klarheit
      calendar: [{}, {}, {}, {}, {}, {}, {}],
      activeCount: 0,
      activeIndex: null,
      selectedDayData: null,
      storyBackgroundColor: "#f4f6f8",
      showGFHours: false,
      fetchingGFHours: false,
      GFHourInfos: { list: [], totalDuration: 0, totalDurationInHours: 0 },
      groupFitnessExecutions: [],
      datePickerDialog: false,
      events: [],
      activeDate: this.$moment().format("YYYY-MM-DD"),
    }
  },
  async created() {
    await this.createCalendar()
    await this.fetchExtraData()
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["toggleNavigationBarVisibility"]),

    hasAnythingToDo(day) {
      if (!day) return false
      return day.checkin || day.workouts?.length || day.courseArray?.length
    },

    hasActivity(day) {
      if (!day) return false
      return day.foundWorkouts?.length || day.courseArray?.length || day.courses?.length || day.checkins?.length
    },

    async createCalendar() {
      let calendarData = await api.fetchTrainingCalendar()
      this.calendar = calendarData.calendar
      let aCount = 0
      this.calendar.forEach((e) => {
        if (this.hasActivity(e)) aCount++
      })
      this.activeCount = (100 / 7) * aCount
    },

    async fetchExtraData() {
      let data = await api.fetchCompleteTrainingCalendar()
      this.groupFitnessExecutions = data.groupFitnessExecutions
      this.events = data.events
    },

    // 🔄 Methode überarbeitet für sofortiges Feedback
    async selectDay(day, index) {
      if (this.activeIndex === index && !this.showGFHours) {
        this.closeDetails()
        return
      }

      this.toggleNavigationBarVisibility()
      this.activeIndex = index
      this.isLoadingDayDetails = true
      // Detailansicht sofort öffnen mit Platzhalter-Daten
      this.selectedDayData = { date: day.ts * 1000 }

      try {
        const dateString = this.$moment(day.ts * 1000).format("YYYY-MM-DD")
        this.activeDate = dateString
        const dayData = await api.fetchTrainingDataForDay(dateString)

        // Geladene Daten mit bestehenden mergen
        this.selectedDayData = { ...dayData, date: day.ts * 1000 }
        this.storyBackgroundColor = this.hasAnythingToDo(this.selectedDayData) ? "#f2fdf2" : "#fafafa"

        if (this.showGFHours) {
          await this.fetchGFHours(dateString)
        }
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Tagesdetails.")
        this.closeDetails() // Bei Fehler schliessen
      } finally {
        this.isLoadingDayDetails = false
      }
    },

    closeDetails() {
      this.toggleNavigationBarVisibility()
      this.selectedDayData = null
      this.activeIndex = null
      this.showGFHours = false
    },

    openDatePicker() {
      if (this.selectedDayData) {
        this.activeDate = this.$moment(this.selectedDayData.date).format("YYYY-MM-DD")
      }
      this.datePickerDialog = true
    },

    async onDateSelected(date) {
      this.datePickerDialog = false
      this.isLoadingDayDetails = true

      if (!this.selectedDayData) {
        this.toggleNavigationBarVisibility()
      }
      // Detailansicht sofort mit Datum öffnen
      this.selectedDayData = { date: this.$moment(date, "YYYY-MM-DD").valueOf() }

      try {
        this.activeDate = date
        const dayData = await api.fetchTrainingDataForDay(date)

        this.selectedDayData = { ...dayData, date: this.$moment(date, "YYYY-MM-DD").valueOf() }
        this.storyBackgroundColor = this.hasAnythingToDo(this.selectedDayData) ? "#f2fdf2" : "#fafafa"

        const newActiveIndex = this.calendar.findIndex((d) => this.$moment(d.ts * 1000).format("YYYY-MM-DD") === date)
        this.activeIndex = newActiveIndex !== -1 ? newActiveIndex : null
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Tagesdetails.")
        this.closeDetails()
      } finally {
        this.isLoadingDayDetails = false
      }
    },

    showGFCalendar() {
      this.showGFHours = !this.showGFHours
      if (this.showGFHours) {
        this.fetchGFHours(this.$moment(this.selectedDayData.date).format("YYYY-MM-DD"))
      }
    },

    async fetchGFHours(date) {
      this.fetchingGFHours = true
      this.GFHourInfos = { list: [], totalDuration: 0, totalDurationInHours: 0 }
      try {
        let startDate = this.$moment(date).startOf("month").unix()
        let endDate = this.$moment(date).endOf("month").unix()
        this.GFHourInfos = await api.getGFHoursForSelf(startDate, endDate)
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Stunden")
      } finally {
        this.fetchingGFHours = false
      }
    },

    getStarCountForCourse(courseId) {
      if (!this.groupFitnessExecutions[courseId] || !this.groupFitnessExecutions[courseId].ratings.length) {
        return 0
      }
      let stars = 0
      this.groupFitnessExecutions[courseId].ratings.forEach((rating) => {
        stars += rating
      })
      return stars / this.groupFitnessExecutions[courseId].ratings.length
    },
  },
  computed: {
    // 🎨 Verbesserte Logik für die Aufenthaltsdauer
    trainingDuration() {
      if (!this.selectedDayData?.checkin) return ""
      if (!this.selectedDayData?.checkout) return "Check-out fehlt"

      const start = this.$moment(this.selectedDayData.checkin)
      const end = this.$moment(this.selectedDayData.checkout)
      const diff = this.$moment.duration(end.diff(start))
      const hours = Math.floor(diff.asHours())
      const minutes = diff.minutes()

      if (hours > 0) {
        return `Aufenthalt: ${hours} Std. ${minutes} Min.`
      }
      return `Aufenthalt: ${minutes} Min.`
    },

    combinedActivities() {
      if (!this.selectedDayData) return []

      const courses = (this.selectedDayData.courseArray || []).map((c) => ({
        type: "Group Fitness",
        title: c.course.name,
        image: "/img/kurse/" + c.course.bild,
        duration: c.time,
        id: c.course._id,
      }))

      const workouts = (this.selectedDayData.workouts || []).map((w) => ({
        type: "Trainingsplan",
        title: w.title,
        image: "/img/uebungen/" + w.image,
        duration: w.duration,
        caloriesBurned: w.caloriesBurned,
        liftedWeight: w.liftedWeight,
        id: w._id,
      }))

      return [...courses, ...workouts]
    },
  },
}
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&display=swap");

// --- VARIABLEN FÜR LIGHT MODE ---
$primary-color: var(--v-primary, #52ae32);
$font-family: "Poppins", sans-serif;
$glow-color: rgba($primary-color, 0.75);
$background-color: #f4f6f8;
$text-color: #2c3e50;
$card-background: #ffffff;
$subtle-border: #e0e0e0;

.training-story-view {
  font-family: $font-family;
  background-color: white;
  color: $text-color;
  padding-bottom: 20px;
}

// --- Kalender Header ---
.calendar-header {
  display: flex;
  justify-content: space-around;
  padding: 16px 8px;
  max-width: 500px;
  margin: 0 auto;
}

.day-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;

  .day-name {
    color: #888;
    font-size: 0.8rem;
    margin-bottom: 8px;
    font-weight: 600;
  }

  .day-button {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-color: #eef0f2;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: 700;
    font-size: 1.1rem;
    transition: all 0.3s ease;
    border: 2px solid transparent;
    color: $text-color;

    &--today {
      border-color: rgba(0, 0, 0, 0.2);
    }

    &--active {
      background-color: $primary-color;
      transform: scale(1.15);
      color: #fff;
      box-shadow: 0 0 15px $glow-color, 0 0 25px $glow-color;

      .activity-icon {
        color: #fff;
      }
    }
  }

  .activity-icon {
    color: $primary-color;
    font-size: 1.8rem;
    animation: pulse 2s infinite;
  }
}

// --- Progress Bar ---
.progress-container {
  height: 4px;
  background-color: #e0e0e0;
  border-radius: 2px;
  max-width: 480px;
  margin: 0 auto 16px auto;
  overflow: hidden;

  .progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #52ae32, #6fcf4a);
    border-radius: 2px;
    transition: width 0.5s ease-in-out;
  }
}

// --- Story Detailansicht ---
.story-details-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  z-index: 100;
  overflow-y: auto;
  padding: 20px;
  padding-top: 60px;
  transition: background-color 0.5s ease;

  .close-button {
    color: $text-color;
    z-index: 101;
  }

  .story-content {
    max-width: 500px;
    margin: 0 auto;
  }
}

// --- 🆕 Lade-Overlay ---
.loading-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100% - 60px); // Volle Höhe abzüglich des Paddings
}

.story-title-container {
  margin-bottom: 12px;
}

.story-title {
  font-size: 1.8rem;
  font-weight: 800;
  text-align: center;
  margin: 0;
}

// --- Instructor Actions ---
.instructor-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 24px;
}
.gf-hours-view {
  .gf-title {
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    margin-bottom: 16px;
  }
  .gf-summary {
    font-weight: 600;
    text-align: center;
    padding: 8px;
    background-color: #e9e9e9;
    border-radius: 8px;
    margin-bottom: 16px;
  }
}

// --- Activity Karten ---
.activity-card {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 20px;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 20px;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);

  .rating-info-icon {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 4;
    background-color: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
    padding: 4px;
    color: white;
    cursor: help;
  }

  .card-background-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;
  }

  .card-overlay-gradient {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.8) 100%);
    z-index: 2;
  }

  .card-content {
    position: relative;
    z-index: 3;

    .activity-type {
      background-color: $primary-color;
      padding: 4px 8px;
      border-radius: 8px;
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      display: inline-block;
      margin-bottom: 8px;
    }

    h3 {
      font-size: 1.6rem;
      font-weight: 800;
      line-height: 1.2;
      margin-bottom: 12px;
      text-shadow: 1px 1px 5px rgba(0, 0, 0, 0.5);
    }
  }
}

// --- ✨ Neugestaltete Check-in Karte ---
.checkin-card {
  min-height: auto;
  flex-direction: row;
  align-items: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  color: $text-color;
  padding: 24px;
  justify-content: flex-start;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

  &::after {
    content: "\f0d38"; /* mdi-map-marker-check */
    font-family: "Material Design Icons";
    position: absolute;
    right: -20px;
    bottom: -30px;
    font-size: 10rem;
    color: rgba(0, 0, 0, 0.03);
    transform: rotate(-15deg);
    z-index: 1;
  }

  .checkin-icon {
    font-size: 2.8rem;
    color: $primary-color;
    margin-right: 20px;
  }

  .card-content {
    h3 {
      font-size: 1rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: $primary-color;
      margin: 0 0 4px 0;
      line-height: 1;
      text-shadow: none;
    }
    .time-range {
      font-size: 1.1rem;
      font-weight: 600;
      margin: 0;
    }
    .duration {
      font-weight: 400;
      color: #777;
      font-size: 0.9rem;
      margin-top: 2px;
    }
  }
}

.no-activity-card {
  background-color: #f5f5f5;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  border: 1px dashed $subtle-border;

  .no-activity-icon {
    font-size: 4rem;
    color: $primary-color;
    margin-bottom: 16px;
  }
  h3 {
    font-weight: 700;
    margin-bottom: 8px;
  }
  p {
    color: #888;
  }
}

// --- Stats ---
.stats-grid {
  display: flex;
  gap: 16px;
  margin-top: 16px;
  flex-wrap: wrap;

  .stat-item {
    display: flex;
    align-items: center;
    background: rgba(0, 0, 0, 0.4);
    padding: 4px 10px;
    border-radius: 12px;
    backdrop-filter: blur(5px);

    lottie-player {
      width: 35px;
      height: 35px;
      margin-right: 4px;
      animation: pulse-light 2.5s infinite ease-in-out;
    }

    span {
      font-weight: 700;
      font-size: 0.9rem;
    }
  }
}

// --- Animationen ---
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
}

@keyframes pulse-light {
  0% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.5);
  }
  100% {
    filter: brightness(1);
  }
}
</style>
